import { useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'
import { useLoansListRevalidate } from 'modules/pools/hooks/useLoansList'
import { useTransactionDetails } from 'modules/blockChain/hooks/useTransactionDetails'

import { SentTransaction } from 'modules/pools/ui/SentTransaction'
import { InfoFieldValue } from 'shared/ui/common/InfoFieldValue'

import { ContractRociPayment } from 'modules/contracts/contracts'
import { createRoute } from 'modules/router/utils/createRoute'
import { weiToStr } from 'modules/blockChain/utils/parseWei'
import {
  getERCAssetByAddress,
  PoolAsset,
} from 'modules/pools/constants/poolAssets'
import { PageLoader } from 'shared/ui/layout/PageLoader'
import { FormBorrowWarning } from 'modules/pools/ui/FormBorrowWarning'

type LocationState = null | {
  asset: PoolAsset
  principal: string
}

// loan.status description (as of 19.07.2022)
// 0: UNISSUED
// 1 NEW: is set by default when loan is configured
// 2 APPROVED: is set when loan is fulfilled by the Investor
// 3 PAIDPART: is set after loan is repaid partially
// 4 CLOSED: is repaid in full
// 5 PAIDLATE: is set when repaid after maturity date + grace period
// 6 DEFAULT: is set when loan is liquidated
// 7: LATE: is set for loans which are late, but not paid yet

export function RouteBorrowSent() {
  const location = useLocation()
  const { txHash, loanIdx } = useParams()
  const { walletAddress, chainId } = useWeb3()
  const { receipt } = useTransactionDetails({ hash: txHash })
  const { getAssetDecimals } = useAssetsData()
  const state = location.state as LocationState

  const rociPayment = ContractRociPayment.useContractWeb3()

  const {
    data: loan,
    mutate,
    isLoading,
  } = useSWR(
    receipt && receipt.confirmations >= 1 ? `borrowed-info-${txHash}` : null,
    async () => {
      if (!receipt || !walletAddress) return null
      const loanId = await rociPayment.loanIDs(walletAddress, Number(loanIdx))
      const res = await rociPayment.loanLookup(loanId)
      return res
    },
  )

  const loanAsset = useMemo(() => {
    if (state?.asset) return state.asset

    if (!loan?.ERC20Address) return undefined

    return getERCAssetByAddress(loan.ERC20Address, chainId)
  }, [chainId, loan?.ERC20Address, state?.asset])

  useEffect(() => {
    if (!loan) return
    if (loan.status < 1) {
      setTimeout(() => mutate(), 6000)
    }
  }, [loan, mutate])

  const isLoanApproved = loan && loan.status > 0

  const revalidateLoans = useLoansListRevalidate()

  useEffect(() => {
    if (isLoanApproved) revalidateLoans()
  }, [isLoanApproved, revalidateLoans])

  if (!txHash) {
    return <>Transaction hash is not defined</>
  }

  if (!state && (isLoading || !loan)) {
    return <PageLoader />
  }

  return (
    <>
      <SentTransaction
        txHash={txHash}
        amountToDisplay={
          state?.principal ||
          (loan
            ? weiToStr(loan.principal, getAssetDecimals(loanAsset))
            : undefined)
        }
        amountToken={loanAsset}
        transactionType="Borrow"
        stillInProgress={!isLoanApproved}
        onSuccess={revalidateLoans}
        warning={<FormBorrowWarning />}
      />
      <InfoFieldValue
        label="Is issued"
        value={isLoanApproved ? 'Yes' : 'Not yet'}
      />
    </>
  )
}

export const routeBorrowSent = createRoute({
  headerTitle: 'Borrow',
  layoutType: 'narrow-extended',
  component: RouteBorrowSent,
  checkConnect: true,
})
